<template>
  <div>
    <div class="page-header bordered">
      <div class="row justify-content-between">
        <div class="col-md-8">
          <h1>Technical Integration</h1>
        </div>
        <div class="col-md-4">
          <div class="float-right" v-if="siteConfiguration.enabled && siteConfiguration.domain_name">
            <router-link :to="{ name: 'customEventList', params: {domain_name: siteConfiguration.domain_name}}"
              target="_blank" class="btn btn-light">
              <i class="fa fa-eye" aria-hidden="true"></i>
              Preview
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <form>
      <ShowErrors
        :errorMessages="errorMessages"
      />

      <div class="form-group">
        <b-form-checkbox switch size="lg" v-model="siteConfiguration.enabled"
          vid="enabled">
          Enable technical integration
        </b-form-checkbox>
      </div>

      <div v-show="siteConfiguration.enabled">
        <b-alert show variant="warning">
          <strong>
            After enabling Technical Integration successfully, you will receive an
            email with from Ninkashi.com with steps to follow to finish with integration.
          </strong>
        </b-alert>

        <h3 class="subheadline">Basic Configuration</h3>
        <div class="row">
          <div class="col-md-5">
            <div class="form-group">
              <label for="subdomain">Subdomain</label>
              <select name="subdomain" class="form-control form-control-lg ui-select"
                v-model="siteConfiguration.subdomain" data-vv-as="Subdomain"
                v-validate="'required'">
                <option value="null"> Select subdomain... </option>
                <option v-for="(subdomain, index) in availableSubdomains"
                  :key="index" :value="subdomain">
                  {{ subdomain }}
                </option>
              </select>
              <span v-show="errors.has('subdomain')" class="input-error">
                {{ errors.first('subdomain') }}
              </span>
              <b-form-text id="password-help-block">
                Only subdoamin name
              </b-form-text>
            </div>
          </div>
          <div class="col-md-7">
            <div class="form-group">
              <label for="domain">Domain</label>
              <input type="text" v-model="siteConfiguration.domain" name="domain"
                class="form-control form-control-lg" v-validate="'required|url'"
                data-vv-as="Domain" @blur="purgeDomain()" @keydown.space.prevent/>
              <span v-show="errors.has('domain')" class="input-error">
                {{ errors.first('domain') }}
              </span>
              <b-form-text id="password-help-block">
                Only domain name like: example.com
              </b-form-text>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-5">
            <div class="form-group">
              <label for="site_title">Site Title</label>
              <input type="text" v-model="siteConfiguration.site_title" name="site_title"
                class="form-control form-control-lg" />
            </div>
          </div>
          <div class="col-md-7">
            <div class="form-group">
              <label for="site_title">Site Icon</label>
              <b-form-file
                v-model="siteConfiguration.icon"
                :state="Boolean(siteConfiguration.icon)"
                placeholder="Choose a file or drop it here..."
                drop-placeholder="Drop file here..."
                class="form-control-lg"
                size="lg"
                accept=".jpg, .png, .ico"
                @change="previewIcon"
              >
              </b-form-file>
              <button type="button" class="btn btn-default btn-inverse" style="margin-top: 5px;"
                @click="removeIcon">
                <i class="icon fa fa-trash"></i> Remove Icon
              </button>
              <img v-if="iconData || siteConfiguration.icon_url" class="preview"
                :src="iconData || siteConfiguration.icon_url" style="margin-left: 10px;">
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-5">
            <div class="form-group">
              <label for="site_title">Email Origin</label>
              <input type="text" v-model="siteConfiguration.email_origin" name="email_origin"
                class="form-control form-control-lg" v-validate="'email'"
                data-vv-as="Email Origin" ref="email_origin" @keydown.space.prevent/>
              <span v-show="errors.has('email_origin')" class="input-error">
                {{ errors.first('email_origin') }}
              </span>
            </div>
          </div>
        </div>

        <h3 class="subheadline">Navbar Configuration</h3>
        <div class="form-group">
          <b-form-checkbox switch size="lg" v-model="siteConfiguration.navbar_enabled">
            Enable Navbar
          </b-form-checkbox>
        </div>

        <div v-show="siteConfiguration.navbar_enabled">
          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="navbar_background_color">Background Color</label>
                <div class="input-group mb-3" ref="navbarcolorpicker"
                  data-origin="displayNavbarColorPicker">
                  <input type="text" class="form-control" @focus="showPicker('displayNavbarColorPicker')"
                    v-model="siteConfiguration.navbar_background_color" readonly/>
                  <div class="input-group-append">
                    <span class="input-group-text">
                      <span class="current-color"
                        :style="'background-color: ' + navbarColor.hex"
                        @click="togglePicker('displayNavbarColorPicker')">
                    </span>
                    <chrome-picker v-if="displayNavbarColorPicker"
                      :value="navbarColor"
                      @input="updateNavbarColorFromPicker" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="navbar_links_color">Link Color</label>
                <div class="input-group mb-3" ref="navbarlinkscolorpicker"
                  data-origin="displayNavbarLinksColorPicker">
                  <input type="text" class="form-control" @focus="showPicker('displayNavbarLinksColorPicker')"
                    v-model="siteConfiguration.navbar_links_color" readonly/>
                  <div class="input-group-append">
                    <span class="input-group-text">
                      <span class="current-color"
                        :style="'background-color: ' + navbarLinksColor.hex"
                        @click="togglePicker('displayNavbarLinksColorPicker')">
                    </span>
                    <chrome-picker v-if="displayNavbarLinksColorPicker"
                      :value="navbarLinksColor"
                      @input="updateNavbarLinksColorFromPicker" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label for="domain">Add Navbar links</label>

            <div class="row">
              <div class="col-md-5">
                <input type="text" v-model="navbarLinkName"
                  class="form-control form-control-lg" placeholder="Link Name"/>
                <b-form-text id="password-help-block">
                  Name that will appear as link on navbar
                </b-form-text>
              </div>

              <div class="col-md-6">
                <input type="text" v-model="navbarLinkUrl"
                  class="form-control form-control-lg" placeholder="Link URL"/>
                <b-form-text id="password-help-block">
                  Add full URL. Including http/https protocol
                </b-form-text>
              </div>

              <div class="col-md-1">
                <b-button v-b-tooltip.hover title="Add Link" class="btn btn-lg btn-primary"
                  v-on:click="addLink('navbarLinkName', 'navbarLinkUrl', 'navbar_links')">
                  <i class="fa fa-plus-square" aria-hidden="true"></i>
                </b-button>
              </div>
            </div>
          </div>

          <ul :show="siteConfiguration.navbar_links.length > 0"
            class="scroll-list">
            <li v-for="(link, index) in siteConfiguration.navbar_links" :key="index">
              <b-link :href="link.url" target="_blank">
                {{ link.name }} ({{ link.url }})
              </b-link>
              &nbsp;&nbsp;
              <a href="javascript:;" v-on:click="removeLink('navbar_links', index)">
                  <i class="fa fa-minus-square" aria-hidden="true"></i>
              </a>
            </li>
          </ul>

          <b-alert :show="siteConfiguration.navbar_links.length === 0">
            Navbar without links
          </b-alert>
        </div>

        <h3 class="subheadline">Body Configuration</h3>
        <div class="form-group">
          <b-form-checkbox switch size="lg" v-model="siteConfiguration.body_enabled"
            :disabled="true">
            Enable Body
          </b-form-checkbox>
        </div>

        <div v-show="siteConfiguration.body_enabled">
          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="body_background_color">Background Color</label>
                <div class="input-group mb-3" ref="bodycolorpicker"
                  data-origin="displayBodyColorPicker">
                  <input type="text" class="form-control" @focus="showPicker('displayBodyColorPicker')"
                    v-model="siteConfiguration.body_background_color" readonly/>
                  <div class="input-group-append">
                    <span class="input-group-text">
                      <span class="current-color"
                      :style="'background-color: ' + bodyColor.hex"
                      @click="togglePicker('displayBodyColorPicker')">
                    </span>
                    <chrome-picker v-if="displayBodyColorPicker"
                      :value="bodyColor"
                      @input="updateBodyColorFromPicker" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="body_link_color">Link Color</label>
                <div class="input-group mb-3" ref="bodylinkcolorpicker"
                  data-origin="displayBodyLinkColorPicker">
                  <input type="text" class="form-control" @focus="showPicker('displayBodyLinkColorPicker')"
                    v-model="siteConfiguration.body_link_color" readonly/>
                  <div class="input-group-append">
                    <span class="input-group-text">
                      <span class="current-color"
                        :style="'background-color: ' + bodyLinkColor.hex"
                        @click="togglePicker('displayBodyLinkColorPicker')">
                    </span>
                    <chrome-picker v-if="displayBodyLinkColorPicker"
                      :value="bodyLinkColor"
                      @input="updateBodyLinkColorFromPicker" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="event_time_color">Event Highlight Color</label>
                <div class="input-group mb-3" ref="eventtimecolorpicker"
                  data-origin="displayEventTimeColorPicker">
                  <input type="text" class="form-control" @focus="showPicker('displayEventTimeColorPicker')"
                    v-model="siteConfiguration.event_time_color" readonly/>
                  <div class="input-group-append">
                    <span class="input-group-text">
                      <span class="current-color"
                      :style="'background-color: ' + eventTimeColor.hex"
                      @click="togglePicker('displayEventTimeColorPicker')">
                    </span>
                    <chrome-picker v-if="displayEventTimeColorPicker"
                      :value="eventTimeColor"
                      @input="updateEventTimeColorFromPicker" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="event_title_color">Event Name Color</label>
                <div class="input-group mb-3" ref="eventtitlecolorpicker"
                  data-origin="displayEventTitleColorPicker">
                  <input type="text" class="form-control" @focus="showPicker('displayEventTitleColorPicker')"
                    v-model="siteConfiguration.event_title_color" readonly/>
                  <div class="input-group-append">
                    <span class="input-group-text">
                      <span class="current-color"
                      :style="'background-color: ' + eventTitleColor.hex"
                      @click="togglePicker('displayEventTitleColorPicker')">
                    </span>
                    <chrome-picker v-if="displayEventTitleColorPicker"
                      :value="eventTitleColor"
                      @input="updateEventTitleColorFromPicker" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="event_title_color">Event Details Color</label>
                <div class="input-group mb-3" ref="eventdetailscolorpicker"
                  data-origin="displayEventDetailsColorPicker">
                  <input type="text" class="form-control" @focus="showPicker('displayEventDetailsColorPicker')"
                    v-model="siteConfiguration.event_details_color" readonly/>
                  <div class="input-group-append">
                    <span class="input-group-text">
                      <span class="current-color"
                      :style="'background-color: ' + eventDetailsColor.hex"
                      @click="togglePicker('displayEventDetailsColorPicker')">
                    </span>
                    <chrome-picker v-if="displayEventDetailsColorPicker"
                      :value="eventDetailsColor"
                      @input="updateEventDetailsColorFromPicker" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h3 class="subheadline">Footer Configuration</h3>
        <div class="form-group">
          <b-form-checkbox switch size="lg" v-model="siteConfiguration.footer_enabled">
            Enable Footer
          </b-form-checkbox>
        </div>

        <div v-show="siteConfiguration.footer_enabled">
          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="footer_background_color">Background Color</label>
                <div class="input-group mb-3" ref="footercolorpicker"
                  data-origin="displayFooterColorPicker">
                  <input type="text" class="form-control" @focus="showPicker('displayFooterColorPicker')"
                    v-model="siteConfiguration.footer_background_color" readonly/>
                  <div class="input-group-append">
                    <span class="input-group-text">
                      <span class="current-color"
                      :style="'background-color: ' + footerColor.hex"
                      @click="togglePicker('displayFooterColorPicker')">
                    </span>
                    <chrome-picker v-if="displayFooterColorPicker"
                      :value="footerColor"
                      @input="updateFooterColorFromPicker" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label for="domain">Add Footer links</label>

            <div class="row">
              <div class="col-md-5">
                <input type="text" v-model="footerLinkName"
                  class="form-control form-control-lg" placeholder="Link Name"/>
                  <b-form-text id="password-help-block">
                    Name that will appear as link on footer
                  </b-form-text>
              </div>

              <div class="col-md-6">
                <input type="text" v-model="footerLinkUrl"
                  class="form-control form-control-lg" placeholder="Link URL"/>
                  <b-form-text id="password-help-block">
                    Add full URL. Including http/https protocol
                  </b-form-text>
              </div>

              <div class="col-md-1">
                <b-button v-b-tooltip.hover title="Add Link" class="btn btn-lg btn-primary"
                  v-on:click="addLink('footerLinkName', 'footerLinkUrl', 'footer_links')">
                  <i class="fa fa-plus-square" aria-hidden="true"></i>
                </b-button>
              </div>
            </div>
          </div>

          <ul :show="siteConfiguration.footer_links.length > 0"
            class="scroll-list">
            <li v-for="(link, index) in siteConfiguration.footer_links" :key="index">
              <b-link :href="link.url" target="_blank">
                {{ link.name }} ({{ link.url }})
              </b-link>
              &nbsp;&nbsp;
              <a v-b-tooltip.hover title="Delete Link" href="javascript:;"
                v-on:click="removeLink('footer_links', index)">
                  <i class="fa fa-minus-square" aria-hidden="true"></i>
              </a>
            </li>
          </ul>

          <b-alert :show="siteConfiguration.footer_links.length === 0">
            Footer without links
          </b-alert>
        </div>

        <br/>
      </div>

      <hr>
      <div class="form-group action">
        <button type="button" class="btn btn-lg btn-primary" v-on:click="validate()">
          Save
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import ShowErrors from '../../common/ShowErrors'
import { Chrome } from 'vue-color'

const objectToFormData = require('object-to-formdata')

export default {
  name: 'SiteIntegrationForm',
  components: {
    ShowErrors,
    'chrome-picker': Chrome
  },
  data: function () {
    return {
      displayNavbarColorPicker: false,
      navbarColor: {
        hex: '#FFFFFF',
        source: 'hex'
      },
      displayNavbarLinksColorPicker: false,
      navbarLinksColor: {
        hex: '#FFFFFF',
        source: 'hex'
      },
      navbarLinkName: '',
      navbarLinkUrl: '',
      displayBodyColorPicker: false,
      bodyColor: {
        hex: '#FFFFFF',
        source: 'hex'
      },
      displayBodyLinkColorPicker: false,
      bodyLinkColor: {
        hex: '#0000FF',
        source: 'hex'
      },
      displayEventTimeColorPicker: false,
      eventTimeColor: {
        hex: '#FFFFFF',
        source: 'hex'
      },
      displayEventDetailsColorPicker: false,
      eventDetailsColor: {
        hex: '#FFFFFF',
        source: 'hex'
      },
      displayEventTitleColorPicker: false,
      eventTitleColor: {
        hex: '#FFFFFF',
        source: 'hex'
      },
      displayFooterColorPicker: false,
      footerColor: {
        hex: '#FFFFFF',
        source: 'hex'
      },
      footerLinkName: '',
      footerLinkUrl: '',
      iconData: '',
      siteConfiguration: {
        enabled: true,
        body_enabled: true,
        navbar_enabled: false,
        navbar_background_color: '',
        body_background_color: '',
        body_link_color: '',
        footer_enabled: false,
        footer_background_color: '',
        event_details_color: '',
        event_time_color: '',
        event_title_color: '',
        domain: null,
        subdomain: null,
        footer_links: [],
        navbar_links: [],
        site_title: null,
        icon: null,
        remove_icon: null,
        email_origin: null
      },
      availableSubdomains: [],
      errorMessages: []
    }
  },
  created () {
    this.getAvailableSubdomains()
    this.getSiteConfiguration()
  },
  methods: {
    getSiteConfiguration () {
      var self = this
      var loader = this.$loading.show()

      this.$http.get('/site_configuration').then(function (response) {
        self.siteConfiguration = response.data
        self.navbarColor['hex'] = self.siteConfiguration.navbar_background_color
        self.navbarLinksColor['hex'] = self.siteConfiguration.navbar_links_color
        self.bodyColor['hex'] = self.siteConfiguration.body_background_color
        self.bodyLinkColor['hex'] = self.siteConfiguration.body_link_color
        self.eventDetailsColor['hex'] = self.siteConfiguration.event_details_color
        self.eventTimeColor['hex'] = self.siteConfiguration.event_time_color
        self.eventTitleColor['hex'] = self.siteConfiguration.event_title_color
        self.footerColor['hex'] = self.siteConfiguration.footer_background_color
      }).finally(function () {
        loader.hide()
      })
    },
    getAvailableSubdomains () {
      var self = this

      this.$http.get('/site_configurations/available_subdomains').then(function (response) {
        self.availableSubdomains = response.data
      })
    },
    purgeDomain () {
      var domain = this.siteConfiguration.domain.replace(/www.|https:\/\/|http:\/\//g, '')
      this.siteConfiguration.domain = domain
    },
    showPicker (origin) {
      document.addEventListener('click', this.documentClick.bind(this, origin))
      this[origin] = true
    },
    hidePicker (origin) {
      document.removeEventListener('click', this.documentClick.bind(this, origin))
      this[origin] = false
    },
    togglePicker (origin) {
      this[origin] ? this.hidePicker(origin) : this.showPicker(origin)
    },
    updateNavbarColorFromPicker (color) {
      this.navbarColor = color
      this.siteConfiguration.navbar_background_color = color.hex
    },
    updateNavbarLinksColorFromPicker (color) {
      this.navbarLinksColor = color
      this.siteConfiguration.navbar_links_color = color.hex
    },
    updateBodyColorFromPicker (color) {
      this.bodyColor = color
      this.siteConfiguration.body_background_color = color.hex
    },
    updateBodyLinkColorFromPicker (color) {
      this.bodyLinkColor = color
      this.siteConfiguration.body_link_color = color.hex
    },
    updateFooterColorFromPicker (color) {
      this.footerColor = color
      this.siteConfiguration.footer_background_color = color.hex
    },
    updateEventTimeColorFromPicker (color) {
      this.eventTimeColor = color
      this.siteConfiguration.event_time_color = color.hex
    },
    updateEventDetailsColorFromPicker (color) {
      this.eventDetailsColor = color
      this.siteConfiguration.event_details_color = color.hex
    },
    updateEventTitleColorFromPicker (color) {
      this.eventTitleColor = color
      this.siteConfiguration.event_title_color = color.hex
    },
    removeIcon () {
      this.iconData = ''
      this.siteConfiguration['icon'] = null
      this.siteConfiguration['icon_url'] = ''
      this.siteConfiguration['remove_icon'] = true
    },
    previewIcon: function (event) {
      // Reference to the DOM input element
      var input = event.target
      // Ensure that you have a file before attempting to read it
      if (input.files && input.files[0]) {
        var reader = new FileReader()
        reader.onload = (e) => {
          this.iconData = e.target.result
        }
        reader.readAsDataURL(input.files[0])

        this.siteConfiguration['icon'] = input.files[0]
        this.siteConfiguration['remove_icon'] = false
      }
    },
    documentClick (origin, event) {
      var el = null
      if (origin === 'displayFooterColorPicker') {
        el = this.$refs.footercolorpicker
      } else if (origin === 'displayBodyColorPicker') {
        el = this.$refs.bodycolorpicker
      } else if (origin === 'displayBodyLinkColorPicker') {
        el = this.$refs.bodylinkcolorpicker
      } else if (origin === 'displayEventTimeColorPicker') {
        el = this.$refs.eventtimecolorpicker
      } else if (origin === 'displayEventTitleColorPicker') {
        el = this.$refs.eventtitlecolorpicker
      } else if (origin === 'displayEventDetailsColorPicker') {
        el = this.$refs.eventdetailscolorpicker
      } else if (origin === 'displayNavbarLinksColorPicker') {
        el = this.$refs.navbarlinkscolorpicker
      } else {
        el = this.$refs.navbarcolorpicker
      }
      var target = event.target

      if (el !== target && !el.contains(target)) {
        this.hidePicker(origin)
      }
    },
    addLink (linkNameOrigin, LinkUrlOrigin, linkListOrigin) {
      if (this[linkNameOrigin].length > 0 && this[LinkUrlOrigin].length > 0) {
        if (this.isValidURL(this[LinkUrlOrigin])) {
          this.siteConfiguration[linkListOrigin].push({
            name: this[linkNameOrigin],
            url: this[LinkUrlOrigin]
          })

          this[linkNameOrigin] = ''
          this[LinkUrlOrigin] = ''
        } else {
          this.$swal({
            title: 'Invalid URL',
            type: 'warning'
          })
        }
      }
    },
    removeLink (origin, index) {
      this.siteConfiguration[origin].splice(index, 1)
    },
    validate () {
      var self = this

      if (this.siteConfiguration.enabled) {
        this.$validator.validateAll().then((result) => {
          if (result) {
            self.updateSiteConfiguration()
          } else {
            self.$swal({
              title: 'Error saving Technical Integration',
              text: 'Please, check errors in form',
              type: 'error'
            })
          }
        })
      } else {
        self.updateSiteConfiguration()
      }
    },
    updateSiteConfiguration () {
      this.errorMessages = []
      var self = this
      var loader = this.$loading.show()

      const formData = objectToFormData(this.siteConfiguration, null, null, 'site_configuration')

      this.$http.put('/site_configurations', formData)
        .then(function (response) {
          self.siteConfiguration = response.data

          self.$swal({
            title: 'Technical integration saved!',
            type: 'success'
          }).then(function () {
            if (!self.siteConfiguration.dns_config_email_sent && self.siteConfiguration.enabled) {
              self.$swal({
                title: 'Technical Integration enabled successfully!',
                text: 'You will receive an email from Ninkashi.com with neccessary ' +
                  'info to confirm your domain ownership and finish with the configuration.',
                type: 'info'
              })
            }
          })
        }).catch(function (error) {
          if (error) {
            var errorMsg = error.response.data.message || error.response.data.error
            self.errorMessages = errorMsg

            self.$swal({
              title: 'Error updating Technical Integration',
              text: 'Please, check errors in form',
              type: 'error'
            })
          }
        }).finally(function () {
          loader.hide()
        })
    }
  }
}
</script>
